// client/src/pages/AchatsPage.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import {
  TableContainer,
  Paper,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Autocomplete,
  Box,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Snackbar,
  Alert,
  Backdrop,
  InputAdornment,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Search, Edit, Trash2, Receipt, ArrowUp, ArrowDown, GripVertical, X, ShoppingCart, Boxes, ShoppingBag, AlertTriangle, CheckCircle, Copy, Settings, Link } from 'lucide-react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  createColumnHelper,
  flexRender,
} from "@tanstack/react-table";
import ProductTools from "../components/ProductTools"; // ajustez le chemin selon votre arborescence
import CardMetric from "../components/CardMetric";
import ParametrageFraisModal from "../components/ParametrageFraisModal";
import api from '../utils/api';
import AchatForm from '../components/AchatForm';

// Fonction helper pour convertir une date ISO en YYYY-MM-DD
const formatDateForInput = (dateStr) => {
  if (!dateStr) return "";
  // Si la date est au format ISO (avec T), on la convertit
  if (dateStr.includes('T')) {
    return dateStr.split('T')[0];
  }
  // Sinon on retourne la date telle quelle
  return dateStr;
};

const formatDecimal = (val) => {
  if (val === '' || isNaN(val)) return val;
  return Number(val).toFixed(2);
};

const AchatsPage = () => {
  // Données de l'API
  const [achats, setAchats] = useState([]);
  const [loading, setLoading] = useState(false);
  // Données associées
  const [fournisseurs, setFournisseurs] = useState([]);
  const [produits, setProduits] = useState([]);
  const [statuts, setStatuts] = useState([]);
  // Etats pour les commandes (pop-up)
  const [openOrdersDialog, setOpenOrdersDialog] = useState(false);
  const [orders, setOrders] = useState([]);
  const [selectedAchatForOrders, setSelectedAchatForOrders] = useState(null);

  // États de filtrage, pagination et tri
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
  const [sorting, setSorting] = useState([{ id: "dateAchat", desc: true }]);
  
  // Nouveaux états pour le redimensionnement et l'ordre des colonnes
  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem('achatsColumnOrder');
    return savedOrder ? JSON.parse(savedOrder) : [];
  });
  const [columnSizing, setColumnSizing] = useState(() => {
    const savedSizing = localStorage.getItem('achatsColumnSizing');
    if (savedSizing) {
      return JSON.parse(savedSizing);
    }
    // Valeurs par défaut
    return {
      dateAchat: 120,
      SKUID: 150,
      Produit: 250,
      Fournisseur: 150,
      Quantité: 100,
      stockRestant: 120,
      commandes: 100,
      tools: 100,
      prixAchat: 120,
      Statut: 120,
      actions: 120,
      dateLivraison: 120,
      frais_preparation: 120,
      frais_livraison: 120,
      URL: 200
    };
  });
  const [draggedColumnId, setDraggedColumnId] = useState(null);
  
  // État pour la sélection et le survol des lignes
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  
  // État pour les filtres de colonnes
  const [filters, setFilters] = useState({
    dateAchat: { operator: '=', value: '' },
    SKUID: { value: '' },
    Produit: { value: '' },
    Fournisseur: { value: '' },
    stockRestant: { operator: '>=', value: '' },
    Statut: { value: '' },
  });

  // États pour le formulaire d'ajout / édition
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    dateAchat: "",
    SKUID: "",
    Fournisseur: "",
    Quantité: "",
    prixAchat: "",
    stockRestant: "",
    Statut: "",
    Produit: "",
    dateLivraison: "",
    frais_preparation: "",
    frais_livraison: "",
    TVADeductible: false,
    TxTVA: "20",
    URL: ""
  });
  const [editingId, setEditingId] = useState(null);

  // États pour le détail produit
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // États pour la confirmation de suppression
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  
  // État pour la boîte de dialogue de détail des achats
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [openStockAmazonDialog, setOpenStockAmazonDialog] = useState(false);

  // États pour le processus de création de listing
  const [isCreatingListing, setIsCreatingListing] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' // 'success', 'error', 'info', 'warning'
  });

  // État pour afficher/masquer la recherche globale
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);

  // État pour le paramétrage des frais
  const [openParametrageModal, setOpenParametrageModal] = useState(false);

  // Ajout d'un état pour stocker les valeurs par défaut de parametrage
  const [defaultFrais, setDefaultFrais] = useState({ frais_preparation: '', frais_livraison: '' });

  // Charger les valeurs par défaut une seule fois au montage
  useEffect(() => {
    const fetchDefaultFrais = async () => {
      try {
        const res = await api.get('/api/parametrage');
        if (res.data) {
          setDefaultFrais({
            frais_preparation: res.data.frais_preparation !== undefined && res.data.frais_preparation !== null
              ? Number(res.data.frais_preparation).toFixed(2)
              : '',
            frais_livraison: res.data.frais_livraison !== undefined && res.data.frais_livraison !== null
              ? Number(res.data.frais_livraison).toFixed(2)
              : '',
          });
        }
      } catch (e) {
        setDefaultFrais({ frais_preparation: '', frais_livraison: '' });
      }
    };
    fetchDefaultFrais();
  }, []);

  // Fonctions pour le détail produit
  const handleOpenProductDetail = (productId) => {
    const product = produits.find((p) => p.id === productId);
    if (product) {
      setSelectedProduct(product);
      setOpenProductDialog(true);
    }
  };

  const handleCloseProductDialog = () => {
    setOpenProductDialog(false);
    setSelectedProduct(null);
  };

  // Fonction pour calculer le montant d'un achat
  const calculateAchatAmount = (achat) => {
    const prixAchat = Number(achat.prixAchat) || 0;
    const quantite = Number(achat.Quantité) || 0;
    return prixAchat * quantite;
  };

  // Fonction pour mettre à jour les données
  const handleDataUpdate = useCallback((data) => {
    // Cette fonction peut être utilisée pour mettre à jour d'autres états si nécessaire
  }, []);

  // Chargement des données depuis l'API
  const fetchAchats = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/achats");
      const data = Array.isArray(res.data) ? res.data : [];
      // S'assurer que les champs frais_preparation et frais_livraison sont bien présents
      setAchats(data.map(a => ({
        ...a,
        frais_preparation: a.frais_preparation !== undefined && a.frais_preparation !== null ? a.frais_preparation : '',
        frais_livraison: a.frais_livraison !== undefined && a.frais_livraison !== null ? a.frais_livraison : '',
        URL: a.URL || ''
      })));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFournisseurs = async () => {
    try {
      const res = await api.get("/api/fournisseurs");
      setFournisseurs(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchProduits = async () => {
    try {
      const res = await api.get("/api/produits");
      setProduits(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchStatuts = async () => {
    try {
      const res = await api.get("/api/statut");
      // S'assurer que nous avons un tableau de statuts
      const statutsData = Array.isArray(res.data) ? res.data : 
                         Array.isArray(res.data.data) ? res.data.data :
                         Array.isArray(res.data.data?.data) ? res.data.data.data : [];
      setStatuts(statutsData);
    } catch (err) {
      console.error("Erreur lors de la récupération des statuts:", err);
      setStatuts([]);
    }
  };

  // Ajoutez cette fonction helper en haut du fichier (ou près de formatDateForInput)
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    fetchAchats();
    fetchFournisseurs();
    fetchProduits();
    fetchStatuts();
  }, []);

  // Sauvegarder la configuration des colonnes
  useEffect(() => {
    if (columnOrder.length > 0) {
      localStorage.setItem('achatsColumnOrder', JSON.stringify(columnOrder));
    }
  }, [columnOrder]);

  useEffect(() => {
    if (Object.keys(columnSizing).length > 0) {
      localStorage.setItem('achatsColumnSizing', JSON.stringify(columnSizing));
    }
  }, [columnSizing]);

  // Fonctions d'ouverture/fermeture du formulaire d'ajout/édition
  const handleOpenDialog = () => {
    setFormData({
      dateAchat: "",
      SKUID: "",
      Fournisseur: "",
      Quantité: "",
      prixAchat: "",
      stockRestant: "",
      Statut: "",
      Produit: "",
      dateLivraison: "",
      frais_preparation: defaultFrais.frais_preparation,
      frais_livraison: defaultFrais.frais_livraison,
    });
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const toNumberOrZero = (val) => {
    if (val === '' || val === null || val === undefined) return 0;
    return Number(val);
  };

  const handleSubmit = async (data) => {
    try {
      let achatRes;
      if (editingId) {
        achatRes = await api.put(
          `/api/achats/${editingId}`,
          {
            ...data,
            frais_preparation: toNumberOrZero(data.frais_preparation),
            frais_livraison: toNumberOrZero(data.frais_livraison)
          }
        );
      } else {
        achatRes = await api.post("/api/achats", {
          ...data,
          frais_preparation: toNumberOrZero(data.frais_preparation),
          frais_livraison: toNumberOrZero(data.frais_livraison)
        });
      }

      // Utilise l'ID d'achat existant si en édition, sinon celui renvoyé par le POST
      const achatIdToUse = editingId || achatRes.data.id;

      // Appel vers Amazon si SKUID est vide
      if (!data.SKUID || data.SKUID.trim() === "") {
        try {
          setIsCreatingListing(true);
          // Récupérer les informations du produit
          const produit = produits.find(p => p.id === data.Produit);
          if (!produit) {
            throw new Error("Produit non trouvé");
          }
          if (!produit.ASIN || produit.ASIN.trim() === "") {
            throw new Error("ASIN du produit non trouvé ou vide");
          }

          const amazonRes = await api.post("/api/amazon/createListing", {
            achatId: achatIdToUse,
            productId: data.Produit,
            asin: produit.ASIN.trim(),
            quantity: parseInt(data.Quantité, 10),
            price: parseFloat(data.prixAchat)
          });

          console.log('Réponse Amazon:', amazonRes.data);

          if (amazonRes.data && amazonRes.data.success && amazonRes.data.data && amazonRes.data.data.sku) {
            // Mettre à jour l'achat avec le SKUID reçu
            await api.put(`/api/achats/${achatIdToUse}`, {
              ...data,
              SKUID: amazonRes.data.data.sku
            });

            console.log('Notification de succès déclenchée');
            setNotification({
              open: true,
              message: `Listing créé avec succès sur Amazon ! SKU: ${amazonRes.data.data.sku}`,
              severity: 'success'
            });
          } else {
            console.log('Notification d\'erreur déclenchée - Réponse invalide:', amazonRes.data);
            setNotification({
              open: true,
              message: `Erreur lors de la création du listing: ${amazonRes.data?.data?.message || 'Réponse invalide'}`,
              severity: 'error'
            });
          }
        } catch (amazonError) {
          console.error("Erreur lors de la création du listing Amazon:", amazonError.response?.data || amazonError.message);
          console.log('Notification d\'erreur déclenchée');
          setNotification({
            open: true,
            message: `Erreur lors de la création du listing: ${amazonError.response?.data?.message || amazonError.message}`,
            severity: 'error'
          });
        } finally {
          setIsCreatingListing(false);
        }
      }

      await fetchAchats();
      setOpenDialog(false);
      setEditingId(null);
    } catch (error) {
      console.error(
        "Erreur lors de la création/mise à jour de l'achat :",
        error.response?.data || error.message
      );
      console.log('Notification d\'erreur générale déclenchée');
      setNotification({
        open: true,
        message: `Erreur lors de la création/mise à jour de l'achat: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    }
  };

  const handleCloseNotification = () => {
    console.log('Fermeture de la notification');
    setNotification(prev => ({ ...prev, open: false }));
  };

  const handleEdit = (achat) => {
    console.log("Achat à éditer:", achat); // Pour déboguer
    setFormData({
      dateAchat: achat.dateAchat ? achat.dateAchat.split('T')[0] : "",
      SKUID: achat.SKUID,
      Fournisseur: achat.Fournisseur,
      Quantité: achat.Quantité,
      prixAchat: achat.prixAchat,
      stockRestant: achat.stockRestant,
      Statut: achat.Statut,
      Produit: achat.Produit,
      dateLivraison: achat.dateLivraison,
      frais_preparation: achat.frais_preparation !== undefined && achat.frais_preparation !== null ? achat.frais_preparation : '',
      frais_livraison: achat.frais_livraison !== undefined && achat.frais_livraison !== null ? achat.frais_livraison : '',
      TVADeductible: achat.TVADeductible || false,
      TxTVA: achat.TxTVA || "20",
      URL: achat.URL || ""
    });
    setEditingId(achat.id);
    setOpenDialog(true);
  };

  const handleOpenDeleteDialog = (id) => {
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/api/achats/${deleteId}`);
      fetchAchats();
      setOpenDeleteDialog(false);
      setDeleteId(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setDeleteId(null);
  };

  // Fonctions pour afficher les commandes associées
  const fetchOrders = async () => {
    try {
      const res = await api.get("/api/commandes");
      return Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const handleOpenOrdersDialog = async (achat) => {
    setSelectedAchatForOrders(achat);
    try {
      // On tente d'abord de charger les commandes liées à l'achat via le paramètre "achat"
      let res = await api.get(
        `/api/commandes?achat=${achat.id}`
      );
      let filteredOrders = Array.isArray(res.data) ? res.data : [];
      // Si aucune commande n'est trouvée pour l'achat, on essaye de charger par produit
      if (filteredOrders.length === 0 && achat.Produit) {
        res = await api.get(
          `/api/commandes?produit=${achat.Produit}`
        );
        filteredOrders = Array.isArray(res.data) ? res.data : [];
      }
      setOrders(filteredOrders);
      setOpenOrdersDialog(true);
    } catch (error) {
      console.error("Erreur lors du chargement des commandes :", error);
    }
  };

  const handleCloseOrdersDialog = () => {
    setOpenOrdersDialog(false);
    setOrders([]);
    setSelectedAchatForOrders(null);
  };

  // Pour afficher uniquement la date sans heure/minutes
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString();
  };

  // Mapping pour afficher les libellés
  const getFournisseurLabel = (id) => {
    const fournisseur = fournisseurs.find((f) => f.id === id);
    return fournisseur ? fournisseur.Titre : id;
  };

  const getStatutLabel = (id) => {
    if (!statuts || !Array.isArray(statuts)) return id;
    // Vérifier si l'ID est directement un objet statut
    if (typeof id === 'object' && id !== null) {
      return id.Titre || id;
    }
    // Chercher dans le tableau des statuts
    const statut = statuts.find((s) => {
      if (typeof s === 'object' && s !== null) {
        return s.id === id;
      }
      return false;
    });
    return statut ? statut.Titre : id;
  };

  const getProduitLabel = (id) => {
    const produit = produits.find((p) => p.id === id);
    return produit ? produit.Titre : id;
  };

  // Définition des colonnes avec Tanstack React Table
  const columnHelper = createColumnHelper();
  const columns = useMemo(
    () => [
      columnHelper.accessor("dateAchat", {
        header: "DateAchat",
        cell: (info) => formatDate(info.getValue()),
        sortingFn: "datetime",
        enableColumnFilter: true,
        size: columnSizing.dateAchat,
        enableResizing: true,
        filterFn: 'custom',
      }),
      columnHelper.accessor("dateLivraison", {
        header: "Date de livraison",
        cell: (info) => formatDate(info.getValue()),
        sortingFn: "datetime",
        enableColumnFilter: true,
        size: columnSizing.dateLivraison || 120,
        enableResizing: true,
        filterFn: 'custom',
      }),
      columnHelper.accessor("SKUID", {
        header: "SKUID",
        cell: (info) => info.getValue(),
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.SKUID,
        enableResizing: true,
        filterFn: 'custom',
      }),
      columnHelper.accessor(
        (row) => {
          // Retourne une chaîne contenant Titre, ASIN et EAN
          const product = produits.find((p) => p.id === row.Produit);
          if (product) {
            return `${product.Titre} ${product.ASIN} ${product.EAN}`;
          }
          return "";
        },
        {
          id: "Produit",
          header: "Produit",
          cell: (info) => {
            const product = produits.find(
              (p) => p.id === info.row.original.Produit
            );
            return product ? (
              <div
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
              >
                <span>{product.Titre}</span>
                <IconButton
                  size="small"
                  onClick={() => handleOpenProductDetail(product.id)}
                  title="Voir détail du produit"
                >
                  <Search size={16} />
                </IconButton>
              </div>
            ) : (
              "-"
            );
          },
          enableColumnFilter: true,
          enableSorting: true,
          size: columnSizing.Produit,
          enableResizing: true,
          filterFn: 'equals',
        }
      ),
      columnHelper.accessor((row) => getFournisseurLabel(row.Fournisseur), {
        id: "Fournisseur",
        header: "Fournisseur",
        cell: (info) => info.getValue(),
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.Fournisseur,
        enableResizing: true,
        filterFn: 'equals',
      }),
      columnHelper.accessor("Quantité", {
        header: "Quantité",
        cell: (info) => info.getValue(),
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.Quantité,
        enableResizing: true,
      }),
      // Nouvelle colonne Stock restant
      columnHelper.accessor("stockRestant", {
        header: "Stock restant",
        cell: (info) => info.getValue(),
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.stockRestant,
        enableResizing: true,
        filterFn: 'custom',
      }),
      // Nouvelle colonne Commandes (icône)
      columnHelper.display({
        id: "commandes",
        header: "Commandes",
        cell: (info) => (
          <IconButton
            size="small"
            onClick={() => handleOpenOrdersDialog(info.row.original)}
            title="Voir commandes"
          >
            <ShoppingBag size={16} />
          </IconButton>
        ),
        size: columnSizing.commandes,
        enableResizing: true,
      }),
      columnHelper.display({
        id: "tools",
        header: "Outils",
        cell: (info) => {
          const achat = info.row.original;
          const product = produits.find((p) => p.id === achat.Produit);
          return product && product.ASIN ? (
            <ProductTools asin={product.ASIN} />
          ) : (
            "-"
          );
        },
        size: columnSizing.tools,
        enableResizing: true,
      }),
      columnHelper.accessor("prixAchat", {
        header: "Prix d'achat",
        cell: (info) => {
          const value = Number(info.getValue()) || 0;
          return `${value.toFixed(2)} €`;
        },
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.prixAchat,
        enableResizing: true,
      }),
      columnHelper.accessor("frais_preparation", {
        header: "Frais préparation",
        cell: (info) => {
          const value = Number(info.getValue()) || 0;
          return `${value.toFixed(2)} €`;
        },
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.frais_preparation || 120,
        enableResizing: true,
      }),
      columnHelper.accessor("frais_livraison", {
        header: "Frais livraison",
        cell: (info) => {
          const value = Number(info.getValue()) || 0;
          return `${value.toFixed(2)} €`;
        },
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.frais_livraison || 120,
        enableResizing: true,
      }),
      columnHelper.accessor((row) => getStatutLabel(row.Statut), {
        id: "Statut",
        header: "Statut",
        cell: (info) => info.getValue(),
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.Statut,
        enableResizing: true,
        filterFn: 'equals',
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => (
          <div style={{ display: "flex", gap: "4px" }}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleEdit(info.row.original)}
              title="Éditer"
            >
              <Edit size={16} />
            </IconButton>
            <IconButton
              size="small"
              color="success"
              onClick={() => handleDuplicateAchat(info.row.original)}
              title="Dupliquer"
            >
              <Copy size={16} />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleOpenDeleteDialog(info.row.original.id)}
              title="Supprimer"
            >
              <Trash2 size={16} />
            </IconButton>
          </div>
        ),
        size: columnSizing.actions,
        enableResizing: true,
      }),
      columnHelper.accessor("URL", {
        header: "URL",
        cell: (info) => {
          const url = info.getValue();
          return url ? (
            <Tooltip title={url}>
              <IconButton
                size="small"
                onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                sx={{ color: '#1976d2' }}
              >
                <Link size={16} />
              </IconButton>
            </Tooltip>
          ) : "-";
        },
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.URL || 50,
        enableResizing: true,
        filterFn: 'custom',
      }),
    ],
    [fournisseurs, produits, statuts, columnSizing]
  );

  // Initialiser l'ordre des colonnes
  useEffect(() => {
    if (columns.length > 0 && columnOrder.length === 0) {
      const initialOrder = columns.map(col => col.id);
      setColumnOrder(initialOrder);
    }
  }, [columns, columnOrder]);

  const table = useReactTable({
    data: achats,
    columns,
    state: { 
      globalFilter, 
      pagination, 
      sorting,
      columnOrder,
      columnSizing,
      columnFilters: Object.entries(filters).map(([id, filter]) => {
        return {
          id,
          value: filter
        };
      })
    },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnOrderChange: (newOrder) => {
      setColumnOrder(newOrder);
    },
    onColumnSizingChange: (newSizing) => {
      setColumnSizing(newSizing);
      localStorage.setItem('achatsColumnSizing', JSON.stringify(newSizing));
    },
    onColumnFiltersChange: (updater) => {
      if (typeof updater === 'function') {
        const newFilters = updater(
          Object.entries(filters).map(([id, filter]) => ({
            id,
            value: filter
          }))
        );
        
        const updatedFilters = {};
        newFilters.forEach(filter => {
          updatedFilters[filter.id] = filter.value;
        });
        
        setFilters(updatedFilters);
      } else {
        const updatedFilters = {};
        updater.forEach(filter => {
          updatedFilters[filter.id] = filter.value;
        });
        
        setFilters(updatedFilters);
      }
    },
    globalFilterFn: "includesString",
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableColumnResizing: true,
    enableColumnOrdering: true,
    columnResizeMode: "onChange",
    defaultColumn: {
      minSize: 50,
      maxSize: 500,
      size: 150,
    },
    filterFns: {
      custom: (row, columnId, filterValue) => {
        if (!filterValue || !filterValue.value) return true;
        
        // Traitement spécial pour la DateAchat
        if (columnId === 'dateAchat') {
          const rowDate = row.getValue(columnId);
          if (!rowDate) return false;
          
          // Convertir les dates au format YYYY-MM-DD pour la comparaison
          const rowDateStr = new Date(rowDate).toISOString().split('T')[0];
          return rowDateStr === filterValue.value;
        }
        
        // Traitement spécial pour SKUID
        if (columnId === 'SKUID') {
          const value = row.getValue(columnId);
          if (!value) return false;
          return value.toLowerCase().includes(filterValue.value.toLowerCase());
        }
        
        const value = parseFloat(row.getValue(columnId));
        if (isNaN(value)) return true;

        const filterValueNum = parseFloat(filterValue.value);
        if (isNaN(filterValueNum)) return true;

        switch (filterValue.operator) {
          case '>=':
            return value >= filterValueNum;
          case '>':
            return value > filterValueNum;
          case '<=':
            return value <= filterValueNum;
          case '<':
            return value < filterValueNum;
          case '=':
            return value === filterValueNum;
          default:
            return true;
        }
      },
      equals: (row, columnId, filterValue) => {
        if (!filterValue || !filterValue.value) return true;
        
        const value = row.getValue(columnId);
        
        // Pour les champs texte comme Produit, Fournisseur, Statut
        if (typeof value === 'string') {
          return value.toLowerCase().includes(filterValue.value.toLowerCase());
        }
        
        return value === filterValue.value;
      }
    },
    autoResetPageIndex: false,
  });

  const pageCount = table.getPageCount() || 0;

  // Calcul du montant total des achats avec statut différent de "Expédié"
  const calculateTotalAmount = () => {
    return achats
      .filter(achat => {
        const statutLabel = getStatutLabel(achat.Statut);
        // Exclure "Expédié", "Remboursé" et "Expédié - FBM" avec stock vide
        if (statutLabel === "Expédié" || statutLabel === "Remboursé") return false;
        if (statutLabel === "Expédié - FBM") {
          const stockRestant = Number(achat.stockRestant) || 0;
          return stockRestant > 0;
        }
        return true;
      })
      .reduce((total, achat) => {
        const prixAchat = Number(achat.prixAchat) || 0;
        const quantite = Number(achat.Quantité) || 0;
        return total + (prixAchat * quantite);
      }, 0);
  };

  const totalAmount = calculateTotalAmount();

  // Fonction pour obtenir les achats en cours (statut différent de "Expédié")
  const getAchatsEnCours = () => {
    return achats.filter(achat => {
      const statutLabel = getStatutLabel(achat.Statut);
      // Exclure "Expédié", "Remboursé" et "Expédié - FBM" avec stock vide
      if (statutLabel === "Expédié" || statutLabel === "Remboursé") return false;
      if (statutLabel === "Expédié - FBM") {
        const stockRestant = Number(achat.stockRestant) || 0;
        return stockRestant > 0;
      }
      return true;
    });
  };

  // Fonction pour calculer le total des achats en "Expédié" avec stock
  const calculateTotalStockAmazon = () => {
    const stockAmazonMap = new Map();
    
    achats.forEach(achat => {
      const statutLabel = getStatutLabel(achat.Statut);
      const stockRestant = Number(achat.stockRestant) || 0;
      const isIncluded = (statutLabel === "Expédié" || statutLabel === "Reçu") && stockRestant > 0;
      
      if (isIncluded) {
        const produitId = achat.Produit;
        // On garde toujours le dernier achat pour chaque produit
        if (!stockAmazonMap.has(produitId) || 
            new Date(achat.created_at) > new Date(stockAmazonMap.get(produitId).created_at)) {
          stockAmazonMap.set(produitId, {
            ...achat,
            stockRestant: stockRestant,
            montant: Number(achat.prixAchat) * stockRestant
          });
        }
      }
    });

    const stockAmazon = Array.from(stockAmazonMap.values());
    return stockAmazon.reduce((total, achat) => total + achat.montant, 0);
  };

  // Fonction pour obtenir les achats en "Expédié" avec stock
  const getAchatsStockAmazon = () => {
    const stockAmazonMap = new Map();
    
    achats.forEach(achat => {
      const statutLabel = getStatutLabel(achat.Statut);
      const stockRestant = Number(achat.stockRestant) || 0;
      const isIncluded = (statutLabel === "Expédié" || statutLabel === "Reçu") && stockRestant > 0;
      
      if (isIncluded) {
        const produitId = achat.Produit;
        // On garde toujours le dernier achat pour chaque produit
        if (!stockAmazonMap.has(produitId) || 
            new Date(achat.created_at) > new Date(stockAmazonMap.get(produitId).created_at)) {
          stockAmazonMap.set(produitId, {
            ...achat,
            stockRestant: stockRestant,
            montant: Number(achat.prixAchat) * stockRestant
          });
        }
      }
    });

    return Array.from(stockAmazonMap.values());
  };

  const totalStockAmazon = useMemo(() => calculateTotalStockAmazon(), [achats, statuts]);

  // Mise à jour des données via le callback avec useMemo
  const achatsData = useMemo(() => ({
    coutTotalAchatsEnCours: totalAmount,
    stockAmazon: totalStockAmazon
  }), [totalAmount, totalStockAmazon]);

  // Utilisation de useCallback pour éviter les re-rendus inutiles
  const handleAchatsCardsUpdate = useCallback((data) => {
    handleDataUpdate(data);
  }, [handleDataUpdate]);

  // Fonction pour dupliquer un achat
  const handleDuplicateAchat = async (achat) => {
    try {
      // Trouver le statut "En attente réception"
      const statutEnAttente = statuts.find(s => s.Titre === "En attente réception");
      if (!statutEnAttente) {
        throw new Error("Statut 'En attente réception' non trouvé");
      }

      // Gérer les frais null ou vides
      const frais_preparation = achat.frais_preparation === null || achat.frais_preparation === "" ? 0 : Number(achat.frais_preparation);
      const frais_livraison = achat.frais_livraison === null || achat.frais_livraison === "" ? 0 : Number(achat.frais_livraison);

      // Créer un nouvel achat avec les données modifiées
      const newAchat = {
        dateAchat: new Date().toISOString().split('T')[0],
        SKUID: achat.SKUID,
        Fournisseur: achat.Fournisseur,
        Quantité: achat.Quantité,
        prixAchat: achat.prixAchat,
        stockRestant: achat.stockRestant,
        Statut: statutEnAttente.id,
        Produit: achat.Produit,
        dateLivraison: achat.dateLivraison,
        frais_preparation: frais_preparation,
        frais_livraison: frais_livraison,
        TVADeductible: achat.TVADeductible || false,
        TxTVA: achat.TxTVA || "20",
        URL: achat.URL || ""
      };

      // Envoyer la requête à l'API
      await api.post("/api/achats", newAchat);

      // Rafraîchir les données
      await fetchAchats();

      // Afficher la notification de succès
      setNotification({
        open: true,
        message: "Achat dupliqué avec succès",
        severity: "success"
      });
    } catch (error) {
      console.error("Erreur lors de la duplication de l'achat:", error);
      setNotification({
        open: true,
        message: "Erreur lors de la duplication de l'achat",
        severity: "error"
      });
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <Typography variant="h4" gutterBottom>
        Gestion des Achats
      </Typography>
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <Button variant="contained" color="primary" onClick={handleOpenDialog}>
          {editingId ? "Éditer un Achat" : "Ajouter un Achat"}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => setOpenParametrageModal(true)}
          startIcon={<Settings size={20} />}
        >
          Paramétrage des frais
        </Button>
        <IconButton
          onClick={() => setShowGlobalSearch((prev) => !prev)}
          size="small"
          color={showGlobalSearch ? "primary" : "default"}
        >
          <Search size={20} />
        </IconButton>
        {showGlobalSearch && (
          <TextField
            label="Recherche globale"
            variant="outlined"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            size="small"
            sx={{ flexGrow: 1 }}
          />
        )}
      </div>
      {loading ? (
        <CircularProgress />
      ) : achats.length === 0 ? (
        <Typography>Aucun achat trouvé.</Typography>
      ) : (
        <>
          <div className="flex gap-2 mb-4">
            <CardMetric
              title="Achats en cours"
              value={totalAmount}
              valueColor="text-blue-600"
              icon={<ShoppingCart size={16} />}
              caption={`${getAchatsEnCours().length} produit${getAchatsEnCours().length > 1 ? 's' : ''} en attente`}
            />
            <CardMetric
              title="Stock Amazon"
              value={totalStockAmazon}
              valueColor="text-green-600"
              icon={<Boxes size={16} />}
              caption={`${getAchatsStockAmazon().length} produit${getAchatsStockAmazon().length > 1 ? 's' : ''} en vente`}
            />
          </div>
          <TableContainer component={Paper}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{
                          padding: "6px 8px",
                          borderBottom: "1px solid #ccc",
                          borderRight: "1px solid #eee",
                          textAlign: "left",
                          cursor: header.column.getCanSort() ? "pointer" : "default",
                          position: "relative",
                          width: header.getSize(),
                          minWidth: 50,
                          userSelect: "none",
                          fontSize: "0.875rem",
                        }}
                      >
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            cursor: 'move',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            }
                          }}
                          draggable
                          onDragStart={(e) => {
                            setDraggedColumnId(header.column.id);
                            e.currentTarget.style.opacity = '0.5';
                          }}
                          onDragEnd={(e) => {
                            setDraggedColumnId(null);
                            e.currentTarget.style.opacity = '1';
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            if (draggedColumnId && draggedColumnId !== header.column.id) {
                              const newOrder = table.getAllLeafColumns()
                                .map(col => col.id)
                                .filter(id => id !== draggedColumnId);
                              const targetIndex = newOrder.indexOf(header.column.id);
                              newOrder.splice(targetIndex, 0, draggedColumnId);
                              table.setColumnOrder(newOrder);
                            }
                          }}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <GripVertical 
                            style={{ 
                              marginRight: 4, 
                              opacity: 0.2,
                              fontSize: '1rem',
                              '&:hover': { opacity: 0.5 }
                            }}
                          />
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: <ArrowUp size={16} style={{ marginLeft: 4 }} />,
                            desc: <ArrowDown size={16} style={{ marginLeft: 4 }} />,
                          }[header.column.getIsSorted()] ?? null}
                        </Box>
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            height: '100%',
                            width: '4px',
                            cursor: 'col-resize',
                            userSelect: 'none',
                            touchAction: 'none',
                            backgroundColor: '#e0e0e0',
                            opacity: 0.5,
                            transition: 'opacity 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '1';
                            e.currentTarget.style.backgroundColor = '#2196f3';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '0.5';
                            e.currentTarget.style.backgroundColor = '#e0e0e0';
                          }}
                        />
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                <tr>
                  {table.getHeaderGroups()[0].headers.map((header) => {
                    const columnId = header.column.id;
                    const filter = filters[columnId];
                    
                    if (filter) {
                      // Filtres spéciaux pour Produit, Fournisseur, SKUID et Statut
                      if (columnId === 'Produit' || columnId === 'Fournisseur' || columnId === 'SKUID' || columnId === 'Statut') {
                        // Récupérer les valeurs uniques pour la colonne
                        let uniqueValues = [];
                        
                        if (columnId === 'Produit') {
                          uniqueValues = produits.map(p => p.Titre).filter(Boolean).sort();
                        } else if (columnId === 'Fournisseur') {
                          uniqueValues = fournisseurs.map(f => f.Titre).filter(Boolean).sort();
                        } else if (columnId === 'SKUID') {
                          uniqueValues = [...new Set(achats.map(a => a.SKUID).filter(Boolean))].sort();
                        } else if (columnId === 'Statut') {
                          uniqueValues = statuts.map(s => s.Titre).filter(Boolean).sort();
                        }
                        
                        return (
                          <td
                            key={`filter-${columnId}`}
                            style={{
                              padding: "4px 8px",
                              borderBottom: "1px solid #eee",
                              borderRight: "1px solid #eee",
                              backgroundColor: "#f5f5f5",
                              fontSize: "0.875rem",
                              minHeight: "40px"
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                              <Autocomplete
                                size="small"
                                options={uniqueValues}
                                value={filter.value || ''}
                                onChange={(_, newValue) => {
                                  const newFilter = { ...filter, value: newValue };
                                  setFilters(prev => ({
                                    ...prev,
                                    [columnId]: newFilter
                                  }));
                                  header.column.setFilterValue(newFilter);
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Rechercher..."
                                    size="small"
                                    fullWidth
                                    sx={{ 
                                      '& .MuiInputBase-root': {
                                        height: '36px'
                                      }
                                    }}
                                  />
                                )}
                                freeSolo
                                disableClearable
                                sx={{ width: '100%' }}
                              />
                              {filter.value && (
                                <IconButton 
                                  size="small" 
                                  onClick={() => {
                                    const newFilter = { ...filter, value: '' };
                                    setFilters(prev => ({
                                      ...prev,
                                      [columnId]: newFilter
                                    }));
                                    header.column.setFilterValue(newFilter);
                                  }}
                                  sx={{ ml: 0.5 }}
                                >
                                  <X size={16} />
                                </IconButton>
                              )}
                            </Box>
                          </td>
                        );
                      }
                      
                      // Filtre spécial pour dateAchat
                      if (columnId === 'dateAchat') {
                        return (
                          <td
                            key={`filter-${columnId}`}
                            style={{
                              padding: "4px 8px",
                              borderBottom: "1px solid #eee",
                              borderRight: "1px solid #eee",
                              backgroundColor: "#f5f5f5",
                              fontSize: "0.875rem",
                              minHeight: "40px"
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                              <TextField
                                type="date"
                                size="small"
                                value={filter.value || ''}
                                onChange={(e) => {
                                  const newFilter = { ...filter, value: e.target.value };
                                  setFilters(prev => ({
                                    ...prev,
                                    [columnId]: newFilter
                                  }));
                                  header.column.setFilterValue(newFilter);
                                }}
                                fullWidth
                                sx={{ 
                                  '& .MuiInputBase-root': {
                                    height: '36px'
                                  }
                                }}
                              />
                              {filter.value && (
                                <IconButton 
                                  size="small" 
                                  onClick={() => {
                                    const newFilter = { ...filter, value: '' };
                                    setFilters(prev => ({
                                      ...prev,
                                      [columnId]: newFilter
                                    }));
                                    header.column.setFilterValue(newFilter);
                                  }}
                                  sx={{ ml: 0.5 }}
                                >
                                  <X size={16} />
                                </IconButton>
                              )}
                            </Box>
                          </td>
                        );
                      }
                      
                      // Filtres numériques standard
                      return (
                        <td
                          key={`filter-${columnId}`}
                          style={{
                            padding: "4px 8px",
                            borderBottom: "1px solid #eee",
                            borderRight: "1px solid #eee",
                            backgroundColor: "#f5f5f5",
                            fontSize: "0.875rem",
                            minHeight: "40px"
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', width: '100%' }}>
                            <FormControl size="small" sx={{ minWidth: 60 }}>
                              <Select
                                value={filter.operator}
                                onChange={(e) => {
                                  const newFilter = { ...filter, operator: e.target.value };
                                  setFilters(prev => ({
                                    ...prev,
                                    [columnId]: newFilter
                                  }));
                                  header.column.setFilterValue(newFilter);
                                }}
                                size="small"
                                sx={{ height: '36px' }}
                              >
                                <MenuItem value=">=">≥</MenuItem>
                                <MenuItem value=">">&gt;</MenuItem>
                                <MenuItem value="<=">≤</MenuItem>
                                <MenuItem value="<">&lt;</MenuItem>
                                <MenuItem value="=">=</MenuItem>
                              </Select>
                            </FormControl>
                            <TextField
                              size="small"
                              value={filter.value}
                              onChange={(e) => {
                                const newFilter = { ...filter, value: e.target.value };
                                setFilters(prev => ({
                                  ...prev,
                                  [columnId]: newFilter
                                }));
                                header.column.setFilterValue(newFilter);
                              }}
                              type="number"
                              sx={{ 
                                width: 60,
                                '& .MuiInputBase-root': {
                                  height: '36px'
                                }
                              }}
                            />
                            {filter.value && (
                              <IconButton 
                                size="small" 
                                onClick={() => {
                                  const newFilter = { ...filter, value: '' };
                                  setFilters(prev => ({
                                    ...prev,
                                    [columnId]: newFilter
                                  }));
                                  header.column.setFilterValue(newFilter);
                                }}
                              >
                                <X size={16} />
                              </IconButton>
                            )}
                          </Box>
                        </td>
                      );
                    }
                    return (
                      <td
                        key={`filter-${columnId}`}
                        style={{
                          padding: "4px 8px",
                          borderBottom: "1px solid #eee",
                          borderRight: "1px solid #eee",
                          backgroundColor: "#f5f5f5",
                          fontSize: "0.875rem",
                          minHeight: "40px"
                        }}
                      />
                    );
                  })}
                </tr>
                {table.getRowModel().rows.map((row) => (
                  <tr 
                    key={row.id}
                    onClick={(e) => {
                      // Ne pas sélectionner la ligne si on clique sur un élément interactif
                      if (e.target.closest('button, a, [role="button"], .MuiIconButton-root')) {
                        return;
                      }
                      setSelectedRowId(row.id === selectedRowId ? null : row.id);
                    }}
                    onDoubleClick={() => handleEdit(row.original)}
                    onMouseEnter={() => setHoveredRowId(row.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    style={{
                      backgroundColor: row.id === selectedRowId 
                        ? 'rgba(33, 150, 243, 0.15)' 
                        : row.id === hoveredRowId 
                          ? 'rgba(0, 0, 0, 0.04)' 
                          : 'transparent',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          padding: "6px 8px",
                          borderBottom: "1px solid #eee",
                          borderRight: "1px solid #eee",
                          width: cell.column.getSize(),
                          minWidth: 50,
                          maxWidth: cell.column.getSize(),
                          fontSize: "0.875rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          position: "relative"
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </TableContainer>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <Button
              variant="contained"
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  pageIndex: Math.max(prev.pageIndex - 1, 0),
                }))
              }
              disabled={!table.getCanPreviousPage()}
            >
              Précédent
            </Button>
            <span>
              Page {pagination.pageIndex + 1} sur {pageCount}
            </span>
            <Button
              variant="contained"
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  pageIndex: prev.pageIndex + 1,
                }))
              }
              disabled={!table.getCanNextPage()}
            >
              Suivant
            </Button>
          </div>
        </>
      )}

      {/* Remplacer l'ancien Dialog par le nouveau composant */}
      <AchatForm
        open={openDialog}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        editingId={editingId}
        initialData={formData}
        fournisseurs={fournisseurs}
        produits={produits}
        statuts={statuts}
        isCreatingListing={isCreatingListing}
      />

      {/* Dialog pour afficher le détail du produit */}
      <Dialog
        open={openProductDialog}
        onClose={handleCloseProductDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Détails du Produit</DialogTitle>
        <DialogContent dividers>
          {selectedProduct ? (
            <div style={{ padding: "16px" }}>
              <Typography variant="h5" gutterBottom>
                {selectedProduct.Titre}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>EAN :</strong> {selectedProduct.EAN}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>ASIN :</strong> {selectedProduct.ASIN}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Prix de vente moyen :</strong>{" "}
                {selectedProduct["Prix de vente moyen"]
                  ? Number(selectedProduct["Prix de vente moyen"]).toFixed(2) +
                    " €"
                  : "N/A"}
              </Typography>
            </div>
          ) : (
            <Typography>Aucun produit sélectionné</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseProductDialog}
            variant="contained"
            color="primary"
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmation pour la suppression */}
      <Dialog open={openDeleteDialog} onClose={handleCancelDelete}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>Voulez-vous vraiment supprimer cet achat ?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Non</Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
          >
            Oui
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour afficher les commandes associées */}
      <Dialog
        open={openOrdersDialog}
        onClose={handleCloseOrdersDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Commandes associées</DialogTitle>
        <DialogContent dividers>
          {orders.length === 0 ? (
            <Typography>Aucune commande trouvée pour cet achat.</Typography>
          ) : (
            <TableContainer component={Paper}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "left" }}>
                      AmazonOrderId
                    </th>
                    <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "left" }}>
                      Date commande
                    </th>
                    <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "left" }}>
                      Prix de vente
                    </th>
                    <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "left" }}>
                      Frais FBA
                    </th>
                    <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "left" }}>
                      Frais Referral
                    </th>
                    <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "left" }}>
                      Profit
                    </th>
                    <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "left" }}>
                      ROI
                    </th>
                    <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "left" }}>
                      Marge
                    </th>
                    <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "left" }}>
                      TVA
                    </th>
                    <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "left" }}>
                      Quantité
                    </th>
                    <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "left" }}>
                      Pays
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id}>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                        {order["AmazonOrderId"] || "-"}
                      </td>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                        {order["DateCommande"]
                          ? formatDateDisplay(order["DateCommande"])
                          : "-"}
                      </td>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                        {order["Prix de vente"]
                          ? Number(order["Prix de vente"]).toFixed(2) + " €"
                          : "-"}
                      </td>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                        {order["fraisFBA"]
                          ? Number(order["fraisFBA"]).toFixed(2) + " €"
                          : "-"}
                      </td>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                        {order["fraisReferral"]
                          ? Number(order["fraisReferral"]).toFixed(2) + " €"
                          : "-"}
                      </td>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                        {order["Profit"]
                          ? Number(order["Profit"]).toFixed(2) + " €"
                          : "-"}
                      </td>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                        {order["ROI"]
                          ? Number(order["ROI"]).toFixed(2) + "%"
                          : "-"}
                      </td>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                        {order["Marge"]
                          ? Number(order["Marge"]).toFixed(2) + "%"
                          : "-"}
                      </td>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                        {order["TVA"]
                          ? Number(order["TVA"]).toFixed(2) + " €"
                          : "-"}
                      </td>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                        {order["Quantité"] || "-"}
                      </td>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                        {order["Pays"] || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseOrdersDialog}
            variant="contained"
            color="primary"
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour afficher le détail des achats en cours */}
      <Dialog
        open={openDetailDialog}
        onClose={() => setOpenDetailDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Détail des achats en cours</DialogTitle>
        <DialogContent dividers>
          <TableContainer component={Paper}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "left" }}>
                    Produit
                  </th>
                  <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "right" }}>
                    Quantité
                  </th>
                  <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "right" }}>
                    Montant
                  </th>
                </tr>
              </thead>
              <tbody>
                {getAchatsEnCours().map((achat) => {
                  const produit = produits.find(p => p.id === achat.Produit);
                  const montant = calculateAchatAmount(achat);
                  return (
                    <tr key={achat.id}>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                        {produit ? produit.Titre : "-"}
                      </td>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee", textAlign: "right" }}>
                        {achat.Quantité}
                      </td>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee", textAlign: "right" }}>
                        {montant.toFixed(2)} €
                      </td>
                    </tr>
                  );
                })}
                <tr style={{ fontWeight: 'bold' }}>
                  <td style={{ padding: "8px", borderTop: "2px solid #ccc" }}>
                    Total
                  </td>
                  <td style={{ padding: "8px", borderTop: "2px solid #ccc", textAlign: "right" }}>
                    {getAchatsEnCours().reduce((total, achat) => total + Number(achat.Quantité), 0)}
                  </td>
                  <td style={{ padding: "8px", borderTop: "2px solid #ccc", textAlign: "right" }}>
                    {totalAmount.toFixed(2)} €
                  </td>
                </tr>
              </tbody>
            </table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailDialog(false)} variant="contained" color="primary">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour afficher le détail du stock Amazon */}
      <Dialog
        open={openStockAmazonDialog}
        onClose={() => setOpenStockAmazonDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Détail du stock Amazon</DialogTitle>
        <DialogContent dividers>
          <TableContainer component={Paper}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "left" }}>
                    Produit
                  </th>
                  <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "right" }}>
                    Stock
                  </th>
                  <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "right" }}>
                    Montant
                  </th>
                </tr>
              </thead>
              <tbody>
                {getAchatsStockAmazon().map((achat) => {
                  const produit = produits.find(p => p.id === achat.Produit);
                  const montant = Number(achat.prixAchat) * Number(achat.stockRestant);
                  return (
                    <tr key={achat.id}>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                        {produit ? produit.Titre : "-"}
                      </td>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee", textAlign: "right" }}>
                        {achat.stockRestant}
                      </td>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee", textAlign: "right" }}>
                        {montant.toFixed(2)} €
                      </td>
                    </tr>
                  );
                })}
                <tr style={{ fontWeight: 'bold' }}>
                  <td style={{ padding: "8px", borderTop: "2px solid #ccc" }}>
                    Total
                  </td>
                  <td style={{ padding: "8px", borderTop: "2px solid #ccc", textAlign: "right" }}>
                    {getAchatsStockAmazon().reduce((total, achat) => total + Number(achat.stockRestant), 0)}
                  </td>
                  <td style={{ padding: "8px", borderTop: "2px solid #ccc", textAlign: "right" }}>
                    {totalStockAmazon.toFixed(2)} €
                  </td>
                </tr>
              </tbody>
            </table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStockAmazonDialog(false)} variant="contained" color="primary">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour le paramétrage des frais */}
      <ParametrageFraisModal
        open={openParametrageModal}
        onClose={() => setOpenParametrageModal(false)}
        onSuccess={() => {
          setNotification({
            open: true,
            message: "Paramétrage des frais mis à jour avec succès",
            severity: "success"
          });
        }}
      />

      {/* Notification Snackbar avec une position plus visible */}
      <Snackbar
        open={notification.open}
        autoHideDuration={10000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ 
          '& .MuiAlert-root': {
            minWidth: '300px',
            fontSize: '1rem'
          }
        }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ 
            width: '100%',
            boxShadow: 3,
            '& .MuiAlert-icon': {
              fontSize: '1.5rem'
            }
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AchatsPage;
